import { BaseEntity } from 'src/constants';
import { ApiMethods, ApiTags, appAPI } from '.';
import { notify } from 'src/clients/ApiService';

interface CreateContractFilesResponse {
  fileKey: string;
  pageKeys: string[];
}

export enum FieldType {
  Signature = 'signature',
  Initials = 'initials',
  Date = 'date',
  Text = 'text',
}

export enum InputType {
  Client = 'client',
  Fixed = 'fixed',
  AutoFill = 'autoFill',
  Variable = 'variable',
}
export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
  resizedWidth?: number;
  resizedHeight?: number;
}

export interface ContractField {
  inputType: InputType;
  type: FieldType;
  isOptional: boolean;
  page: number;
  bounds: Bounds;
  label?: string;
  value?: string;
  autoFillField?: string;
}

export interface AddContractTemplateInput {
  name: string;
  fileKey: string;
  fields: ContractField[];
}

export interface ContractTemplate extends BaseEntity {
  id: string;
  name: string;
  fileKey: string;
  fields: ContractField[];
  defaultListIndexPkey: string;
  requestsCount: number;
  submissionsCount: number;
  latestSubmissionDate?: string | null;
}

export const contractsApi = appAPI.injectEndpoints({
  endpoints: build => ({
    createContractFiles: build.mutation<CreateContractFilesResponse, string>({
      query: (fileKey: string) => ({
        path: `/contract-files`,
        method: ApiMethods.post,
        options: {
          body: {
            fileKey,
          },
        },
      }),
    }),
    addContractTemplate: build.mutation<
      ContractTemplate,
      AddContractTemplateInput
    >({
      query: body => ({
        path: `/contract-templates`,
        method: ApiMethods.post,
        options: {
          body,
        },
      }),
      invalidatesTags: [ApiTags.contract_templates],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Contract template created.',
            dispatch,
          });
        } catch (error) {
          notify({
            status: 'error',
            errorMessage: 'The contract template cannot be created.',
            error,
            dispatch,
          });
        }
      },
    }),
    getContractTemplates: build.query<ContractTemplate[], void>({
      query: () => ({
        path: `/contract-templates`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.contract_templates],
    }),
  }),
});

export const {
  useCreateContractFilesMutation,
  useAddContractTemplateMutation,
  useGetContractTemplatesQuery,
} = contractsApi;
